import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Flex } from "antd";
import { User, userQueries } from "@/entities/user";
import { Can } from "@/shared/lib/casl";
import { journalQueryOptions, useJournalMutation } from "@entities/journal";
import { useLocalUser } from "@shared/lib/hooks";
import { extractSignXml, JournalCryptographyWrapper } from "@shared/ui";
import { useGetSigningString } from "../../model/use-get-signing-string";
import { RejectSignModal } from "../reject-sign-modal/reject-sign-modal";

interface SigningButtonProps {
  signerUserId?: string;
  sectionTwoListId: string;
  sectionTwoId: string;
  itemNoVersionId: string;
}

export function SignerButtons({
  signerUserId,
  sectionTwoListId,
  sectionTwoId,
  itemNoVersionId,
}: SigningButtonProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [signingString, setSigningString] = useState<string>("");
  const localUser = useLocalUser();
  const {
    mutateAsync: getSigningString,
    isPending: isGetSigningStringPending,
  } = useGetSigningString({
    sectionTwoListId,
    itemNoVersionId,
  });
  const { mutate: signRecord } = useJournalMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/sign/done",
  );
  const { data: user } = useQuery(userQueries.currentUser());

  const setupSingingString = async (onOpen: any) => {
    if (localUser?.user_id) {
      try {
        const xmlString = await getSigningString({
          signerEmployeeId: localUser?.user_id,
        });
        setSigningString(xmlString);
      } catch {
        /* signing string setup failed */
      }
      onOpen();
    }
  };

  const withSignedString = (
    data: string,
    _proxyId?: string | null,
    mchdId?: string,
  ) => {
    if (!localUser?.user_id || !data) return;
    try {
      signRecord(
        {
          params: { path: { sectionTwoListId, itemNoVersionId } },
          body: {
            signer: { signerEmployeeId: localUser?.user_id },
            signXml: extractSignXml(data),
            ...(mchdId ? { mchdId } : {}),
          },
        },
        {
          onSuccess: () =>
            queryClient.invalidateQueries(
              journalQueryOptions(
                "get",
                "/api/v1/journals/section-two/{sectionTwoId}",
                { params: { path: { sectionTwoId: sectionTwoId! } } },
              ),
            ),
        },
      );
    } catch {
      /* extract/send handled by mutation */
    }
  };

  const handleRejectRecord = () => {
    setOpen(true);
    // rejectRecord({
    //   params: { path: { sectionTwoListId, itemNoVersionId } },
    //   body: { signer: { signerEmployeeId: localUser?.user_id! }, comment: "" },
    // });
  };

  const handleCancelReject = () => {
    setOpen(false);
  };

  return (
    <Flex gap="8px" align="center">
      {localUser?.user_id === signerUserId && (
        <>
          <Can I="sign" a="section_2">
            <JournalCryptographyWrapper
              currentUser={user as User}
              signContent={signingString}
              onSuccess={withSignedString}
            >
              {({ onOpen }) => (
                <Button
                  type="primary"
                  onClick={() => setupSingingString(onOpen)}
                  loading={isGetSigningStringPending}
                >
                  Подписать
                </Button>
              )}
            </JournalCryptographyWrapper>
          </Can>

          <Can I="sign" a="section_2">
            <Button onClick={handleRejectRecord} type="default">
              Отклонить
            </Button>
          </Can>
          <RejectSignModal
            open={open}
            onCancel={handleCancelReject}
            sectionTwoId={sectionTwoId}
            sectionTwoListId={sectionTwoListId}
            itemNoVersionId={itemNoVersionId}
            mode="signer"
          />
        </>
      )}
    </Flex>
  );
}
